import { useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import { Card } from "../ui/card";
import { useForecast } from "../../hooks/queries";
import { useLang } from "../../contexts/LangContext";
import { formatMoney } from "../../lib/api";
import { Activity, AlertTriangle } from "lucide-react";
import { tx } from "../../i18n";
// iter-170e — shared chart helpers + range pill (was duplicated in
// NetWorthChart). Drops CC of this component from 36 → ~18.
import {
  CHART_AXIS_PROPS,
  CHART_TOOLTIP_STYLE,
  formatCompactNumber,
  formatDateTooltip,
  formatDayLabel,
} from "./chartHelpers";
import { RangeSelector } from "./RangeSelector";

const RANGES = [
  { value: 14, label: "14d" },
  { value: 30, label: "30d" },
  { value: 60, label: "60d" },
];

/**
 * Daily cash-flow forecast: bar chart of expected income vs expected payments
 * over the next N days, with a projected cash-balance line overlaid.
 */
// iter-170e — Extracted out of the component to satisfy
// react/no-unstable-nested-components. Pre-existing lint warning.
const BurnReflineLabel = ({ viewBox, lang }) => (
  <g data-testid="forecast-burn-refline">
    <text
      x={viewBox.x}
      y={viewBox.y + 4}
      fill="#DC2626"
      fontSize={10}
      textAnchor="middle"
    >
      {tx(lang, "Burn", "Quema", "Quema")}
    </text>
  </g>
);

export function CashForecastChart() {
  const { lang } = useLang();
  const [days, setDays] = useState(30);
  const { data, isLoading } = useForecast(days);
  const currency = data?.currency || "USD";
  const points = data?.points || [];
  const totals = data?.totals || {};

  const hasFlow = points.some((p) => p.in > 0 || p.out > 0);
  const burnDay = data?.burn_day || null;

  return (
    <Card className="p-5 sm:p-6 rounded-2xl border-slate-200" data-testid="forecast-chart">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center">
            <Activity className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">
              {tx(lang, "Cash burn vs receivables (forecast)", "Quema de caja vs cobros (pronóstico)", "Quema de caja vs cobros (pronóstico)")}
            </h3>
            <p className="text-xs text-slate-500">
              {tx(lang, "Expected receivables weighted by probability, against pending payments. The line projects your daily cash balance.", "Cobros esperados ponderados por probabilidad, contra pagos pendientes. La línea proyecta tu saldo día a día.", "Cobros esperados ponderados por probabilidad, contra pagos pendientes. La línea proyecta tu saldo día a día.")}
            </p>
          </div>
        </div>
        <RangeSelector
          ranges={RANGES}
          value={days}
          onChange={setDays}
          testid="forecast-range"
        />
      </div>

      {/* Totals */}
      <div className="grid sm:grid-cols-3 gap-2 mb-4" data-testid="forecast-totals">
        <Tile
          label={tx(lang, "Expected in", "Entradas esperadas", "Entradas esperadas")}
          value={`+${formatMoney(totals.expected_in || 0, currency)}`}
          tone="pos"
        />
        <Tile
          label={tx(lang, "Expected out", "Salidas previstas", "Salidas previstas")}
          value={`-${formatMoney(totals.expected_out || 0, currency)}`}
          tone="neg"
        />
        <Tile
          label={tx(lang, "Projected ending cash", "Saldo final proyectado", "Saldo final proyectado")}
          value={formatMoney(totals.ending_cash || 0, currency)}
          tone={(totals.ending_cash || 0) < 0 ? "neg" : "neutral"}
          testid="forecast-ending-cash"
        />
      </div>

      {burnDay && (
        <div
          data-testid="forecast-burn-banner"
          className="mb-4 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-sm"
        >
          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
          <span className="text-red-800">
            {tx(lang, "Burn point: ", "Punto de quema: ", "Punto de quema: ")}
            <span className="font-semibold">{new Date(burnDay).toLocaleDateString(tx(lang, "en", "es", "es"))}</span>
            {" "}
            <span className="text-red-700">
              {tx(lang, "— your projected balance crosses zero. Accelerate a receivable or postpone a payment before this date.", "— tu saldo proyectado cruza cero. Acelera un cobro o postpone un pago antes de esta fecha.", "— tu saldo proyectado cruza cero. Acelera un cobro o postpone un pago antes de esta fecha.")}
            </span>
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="h-56 flex items-center justify-center text-slate-400 text-sm">
          {tx(lang, "Loading…", "Cargando…", "Cargando…")}
        </div>
      ) : !hasFlow ? (
        <div
          className="h-56 flex flex-col items-center justify-center text-center text-slate-500 text-sm gap-1 bg-slate-50 rounded-xl border border-dashed border-slate-200"
          data-testid="forecast-empty"
        >
          <span className="font-semibold text-slate-900">
            {tx(lang, "No receivables or payments in this horizon", "No hay cobros ni pagos en este horizonte", "No hay cobros ni pagos en este horizonte")}
          </span>
          <span className="max-w-xs">
            {tx(lang, "Log pending receivables and upcoming payments to see the projection.", "Registra cobros pendientes y pagos próximos para ver la proyección.", "Registra cobros pendientes y pagos próximos para ver la proyección.")}
          </span>
        </div>
      ) : (
        <div className="h-56" style={{ width: "100%", minWidth: 1 }} data-testid="forecast-chart-canvas">
          <ResponsiveContainer width="100%" height={224}>
            <ComposedChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="day"
                tickFormatter={formatDayLabel}
                {...CHART_AXIS_PROPS}
              />
              <YAxis
                tickFormatter={formatCompactNumber}
                {...CHART_AXIS_PROPS}
                width={48}
              />
              <Tooltip
                formatter={(v, key) => [formatMoney(v, currency), key]}
                labelFormatter={formatDateTooltip}
                contentStyle={CHART_TOOLTIP_STYLE}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, color: "#64748B" }}
                formatter={(value) => {
                  if (value === "in") return tx(lang, "In", "Entrada", "Entrada");
                  if (value === "out") return tx(lang, "Out", "Salida", "Salida");
                  if (value === "cash") return tx(lang, "Cash", "Saldo", "Saldo");
                  return value;
                }}
              />
              <ReferenceLine y={0} stroke="#94A3B8" />
              {burnDay && (
                <ReferenceLine
                  x={burnDay}
                  stroke="#DC2626"
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                  // eslint-disable-next-line react/no-unstable-nested-components
                  label={(p) => <BurnReflineLabel {...p} lang={lang} />}
                />
              )}
              <Bar dataKey="in" stackId="a" fill="#16A34A" radius={[3, 3, 0, 0]} />
              <Bar dataKey="out" stackId="b" fill="#DC2626" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="cash" stroke="#0F172A" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function Tile({ label, value, tone, testid }) {
  const color = tone === "pos" ? "text-emerald-700" : tone === "neg" ? "text-red-600" : "text-slate-900";
  return (
    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`font-display text-lg font-bold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
